import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  searchWorkflowTaskObjectGroups,
  SearchWorkflowTaskObjectGroupsResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { Toast } from "../../../../../widget";
import { TasksDashboardGroup, TasksDashboardGroupTasks, TasksDashboardQueryFilters } from "../models";
import {
  buildGroupsRequest,
  buildGroupTasksRequest,
  buildTaskFilter,
  getGroupTasksKey,
  getVisibleStatuses,
  mapNodeToGroup,
  mapNodeToKanbanTask,
} from "../utils";
import { TASKS_DASHBOARD_GROUP_PAGE_SIZE, TASKS_DASHBOARD_PAGE_SIZE } from "./constants";

export interface UseWorkflowTaskGroupsArgs {
  /** The one shared task-level filter object (BR-02828). Must be referentially stable. */
  filters: TasksDashboardQueryFilters;
  /** False in views that show no groups, so they issue no group query at all. */
  enabled: boolean;
  /** Leaf-row page size. */
  pageSize?: number;
  groupPageSize?: number;
  /** Bump to reload the headers and drop the leaf cache. */
  refreshKey?: number;
}

export interface UseWorkflowTaskGroupsReturnData {
  groups: TasksDashboardGroup[];
  isLoadingGroups: boolean;
  groupsHaveNextPage: boolean;
  fetchMoreGroups: () => void;
  /** Leaf rows keyed by `getGroupTasksKey(objectUuid, status)` — `status` omitted means all. */
  groupTasks: Record<string, TasksDashboardGroupTasks>;
  /** Loads a group's first page of rows, unless they are already cached. */
  loadGroupTasks: (objectUuid: string, status?: string) => void;
  /** Loads a group's next page of rows. */
  fetchMoreGroupTasks: (objectUuid: string, status?: string) => void;
}

const EMPTY_GROUP_TASKS: TasksDashboardGroupTasks = {
  tasks: [],
  totalCount: 0,
  hasNextPage: false,
  endCursor: null,
  isLoading: false,
};

/*
  A failed query is surfaced rather than rendered as an empty table — "no tasks" and "we couldn't ask"
  must not look the same. Whatever was already loaded stays on screen.
*/
const GROUPS_ERROR_MESSAGE = "Couldn't load tasks - please try again later.";

const applyGroupTasksPage = (
  groupTasks: TasksDashboardGroupTasks,
  response: SearchWorkflowTasksResponse,
  append: boolean
): TasksDashboardGroupTasks => {
  const tasks = response.results.map(mapNodeToKanbanTask);

  return {
    tasks: append ? [...groupTasks.tasks, ...tasks] : tasks,
    totalCount: response.paging.total,
    hasNextPage: response.paging.hasNextPage,
    endCursor: response.paging.endCursor || null,
    isLoading: false,
  };
};

/**
 * Group-then-rows loading, shared by the List and Gantt views: `searchWorkflowTaskObjectGroups` for
 * the headers and their server-side counts, then `searchWorkflowTasks` per group — lazily, on
 * expand, and cached per `(objectUuid, status)`. Both queries carry the same shared task-level
 * filter, so a header count always describes the rows loaded under it (BR-02828).
 *
 * The two views differ only in how they slice a group: the Gantt loads a whole object (no `status`),
 * the List loads one status section at a time.
 */
export const useWorkflowTaskGroups = ({
  filters,
  enabled,
  pageSize = TASKS_DASHBOARD_PAGE_SIZE,
  groupPageSize = TASKS_DASHBOARD_GROUP_PAGE_SIZE,
  refreshKey = 0,
}: UseWorkflowTaskGroupsArgs): UseWorkflowTaskGroupsReturnData => {
  const [groups, setGroups] = useState<TasksDashboardGroup[]>([]);
  const [paging, setPaging] = useState<{ hasNextPage: boolean; endCursor: string | null }>({
    hasNextPage: false,
    endCursor: null,
  });
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [groupTasks, setGroupTasks] = useState<Record<string, TasksDashboardGroupTasks>>({});

  // The shared task-level filter, built once and reused by the headers and every leaf query.
  const sharedFilter = useMemo(() => buildTaskFilter(filters), [filters]);

  // Monotonic id of the current query, so a response for a superseded filter — or a leaf page whose
  // cursor belongs to the old filter — is discarded rather than applied.
  const loadIdRef = useRef(0);

  /*
    Invalidate during render, not when the header response lands. Consumers that keep a group open
    (the Gantt) re-ask for its rows from an effect, and child effects run before this hook's own —
    so clearing the cache later would drop those rows and leave the group permanently empty. Bumping
    the id here also means a leaf request issued by that effect belongs to the new query, rather than
    being discarded as stale by the header load.
  */
  const queryRef = useRef({ sharedFilter, refreshKey });
  if (queryRef.current.sharedFilter !== sharedFilter || queryRef.current.refreshKey !== refreshKey) {
    const criteriaChanged = queryRef.current.sharedFilter !== sharedFilter;

    queryRef.current = { sharedFilter, refreshKey };
    loadIdRef.current += 1;
    setGroupTasks({});

    /*
      New criteria make the loaded headers describe a task set the user is no longer looking at, so they
      go now rather than when the replacement lands: otherwise the old objects and counts sit under the
      new filter selection while the request is in flight, and stay there for good if it fails (the
      toast alone wouldn't say the numbers on screen belong to a filter that is no longer applied).

      A same-criteria reload (`refreshKey`, e.g. after the task panel saves) keeps them: they still
      describe the current selection, so on failure the view holds what it has and the toast is the
      whole story.
    */
    if (criteriaChanged) {
      setGroups([]);
      setPaging({ hasNextPage: false, endCursor: null });
      /*
        Loading starts here rather than in the effect that issues the request: for the render in between,
        the headers are already cleared while nothing is in flight, and a consumer reading "no rows and
        not loading" takes the view for empty. The dashboard then swaps the whole view out for its empty
        state, which unmounts the Gantt — and remounting it loses everything the chart owns, its
        Timeline/Agenda choice included.
      */
      if (enabled) setIsLoadingGroups(true);
    }
  }

  const searchGroups = useCallback(
    async (afterCursor: string | null): Promise<SearchWorkflowTaskObjectGroupsResponse | undefined> => {
      try {
        const response = await searchWorkflowTaskObjectGroups(buildGroupsRequest(filters, groupPageSize, afterCursor));

        if (response.status === Status.Success && response.data) return response.data;

        Toast.error({ message: GROUPS_ERROR_MESSAGE });
        return undefined;
      } catch {
        Toast.error({ message: GROUPS_ERROR_MESSAGE });
        return undefined;
      }
    },
    [filters, groupPageSize]
  );

  const searchGroupTasks = useCallback(
    async (
      objectUuid: string,
      status: string | undefined,
      afterCursor: string | null
    ): Promise<SearchWorkflowTasksResponse | undefined> => {
      try {
        /*
          A whole-object load (no status slice) still has to honour the hide toggles, so it is
          restricted to the statuses they leave visible — otherwise a group's rows would include
          tasks its own header count excludes. A status-sliced load needs nothing: the slice is
          already one of the visible statuses.
        */
        const visibleStatuses = status === undefined ? getVisibleStatuses(filters) : undefined;

        const response = await searchWorkflowTasks(
          buildGroupTasksRequest(objectUuid, status, sharedFilter, pageSize, afterCursor),
          visibleStatuses && { operator: "in", value: visibleStatuses }
        );

        if (response.status === Status.Success && response.data) return response.data;

        Toast.error({ message: GROUPS_ERROR_MESSAGE });
        return undefined;
      } catch {
        Toast.error({ message: GROUPS_ERROR_MESSAGE });
        return undefined;
      }
    },
    [sharedFilter, filters, pageSize]
  );

  const loadGroups = useCallback(async (): Promise<void> => {
    const loadId = loadIdRef.current;

    setIsLoadingGroups(true);

    const response = await searchGroups(null);

    if (loadId !== loadIdRef.current) return;

    // A failed load leaves the previous headers in place (the failure is toasted) rather than
    // blanking the view into its "no tasks" state.
    if (response) {
      setGroups(response.results.map(mapNodeToGroup));
      setPaging({ hasNextPage: response.paging.hasNextPage, endCursor: response.paging.endCursor || null });
    }

    setIsLoadingGroups(false);
  }, [searchGroups]);

  useEffect(() => {
    if (!enabled) return;

    loadGroups();
  }, [loadGroups, enabled, refreshKey]);

  // Read inside the paging callbacks so cursors and in-flight guards come from current state without
  // the request living in a state updater (which React can invoke twice).
  const pagingRef = useRef(paging);
  pagingRef.current = paging;
  const groupTasksRef = useRef(groupTasks);
  groupTasksRef.current = groupTasks;

  const fetchMoreGroups = useCallback((): void => {
    const { hasNextPage, endCursor } = pagingRef.current;
    if (!hasNextPage) return;

    const loadId = loadIdRef.current;
    setIsLoadingGroups(true);

    searchGroups(endCursor).then((response) => {
      if (loadId !== loadIdRef.current) return;

      if (response) {
        setGroups((current) => [...current, ...response.results.map(mapNodeToGroup)]);
        setPaging({ hasNextPage: response.paging.hasNextPage, endCursor: response.paging.endCursor || null });
      }

      setIsLoadingGroups(false);
    });
  }, [searchGroups]);

  const loadGroupTasksPage = useCallback(
    (objectUuid: string, status: string | undefined, append: boolean): void => {
      const key = getGroupTasksKey(objectUuid, status);
      const current = groupTasksRef.current[key];
      if (current?.isLoading) return;
      if (append && !current?.hasNextPage) return;

      const loadId = loadIdRef.current;
      setGroupTasks((prev) => ({ ...prev, [key]: { ...(prev[key] ?? EMPTY_GROUP_TASKS), isLoading: true } }));

      searchGroupTasks(objectUuid, status, append ? (current?.endCursor ?? null) : null).then((response) => {
        // A reload has replaced the group set (and cleared this cache) — drop the stale page.
        if (loadId !== loadIdRef.current) return;

        setGroupTasks((prev) => ({
          ...prev,
          [key]: response
            ? applyGroupTasksPage(prev[key] ?? EMPTY_GROUP_TASKS, response, append)
            : { ...(prev[key] ?? EMPTY_GROUP_TASKS), isLoading: false },
        }));
      });
    },
    [searchGroupTasks]
  );

  const loadGroupTasks = useCallback(
    (objectUuid: string, status?: string): void => {
      // Already loaded (or loading) from a previous expand of this group — nothing to do. This is
      // the only dedup of an expand: consumers announce open groups freely and rely on it.
      if (groupTasksRef.current[getGroupTasksKey(objectUuid, status)]) return;

      loadGroupTasksPage(objectUuid, status, false);
    },
    // `refreshKey` belongs in the identity even though the body doesn't read it: a consumer holding
    // a group open re-announces it when this callback changes, which is what reloads its rows.
    [loadGroupTasksPage, refreshKey]
  );

  const fetchMoreGroupTasks = useCallback(
    (objectUuid: string, status?: string): void => loadGroupTasksPage(objectUuid, status, true),
    [loadGroupTasksPage]
  );

  return {
    groups,
    isLoadingGroups,
    groupsHaveNextPage: paging.hasNextPage,
    fetchMoreGroups,
    groupTasks,
    loadGroupTasks,
    fetchMoreGroupTasks,
  };
};
